import {
  ATypeCardView,
  ATypeAmenity,
  CTypeMap,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  GOI_WEB_URL,
  BTypeBreadCrumb,
  sangjoFaqRichSearch,
  buildFaqList,
  mergeFaqSchemas,
  RichSearch,
  buildParlorLocalBusinessSchema,
  TrafficInfo,
  HeroDataCard,
  HeroPills,
  ETypeBinsoPriceSummary,
  EstimateSection,
  SupplementarySection,
  ETypeFAQ,
  ETypeCTA,
  customEvent,
  BinsoPriceTable,
  CTypeSangjoService,
  NearbyCrematoria,
  PARLOR_ENRICHMENT_EXCLUDE_IDS,
  excludeParlorList,
  excludeParlorPriceList,
  LEGAL_TEXTS,
  ColorIcon,
  FAQKey,
  ParlorQuickInquiry,
  EskyPriceDetailSection,
  ParlorIntentLinks,
  EskyPriceData,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { getEskyPrice } from "goi_common/src/utils/eskyPrice";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        id: String(parlor.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

const getParlorStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let description = "";

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const binsoCount = parlor.binsos?.length || 0;
      const binsoPyeongs = parlor.binsos?.map((b: any) => b.pyeong) || [];
      const minPyeong = binsoPyeongs.length
        ? Math.round(Math.min(...binsoPyeongs))
        : 0;
      const maxPyeong = binsoPyeongs.length
        ? Math.round(Math.max(...binsoPyeongs))
        : 0;
      const parkCnt = parlor.amenities?.parkcnt || 0;
      const managedType = parlor.managed_type || "";
      const 무빈소 = parlor.expected_charge_sum?.["무빈소"]
        ? Math.round(parlor.expected_charge_sum["무빈소"] / 10000)
        : 0;
      const 일반장 = parlor.expected_charge_sum?.["일반장"]
        ? Math.round(parlor.expected_charge_sum["일반장"] / 10000)
        : 0;

      const descParts = [
        `${parlor.companyname} 비용, 빈소, 이용시설, 위치 정보를 종합 안내합니다`,
        `공공 데이터 기반 · 고이장례연구소`,
        `주소 : ${parlor.address}`,
        managedType ? `${managedType} 장례식장` : "",
        binsoCount ? `빈소 ${binsoCount}실(${minPyeong}~${maxPyeong}평)` : "",
        무빈소 && 일반장 ? `예상 총비용 ${무빈소}만~${일반장}만원` : "",
        parkCnt ? `주차 ${parkCnt}대` : "",
      ].filter(Boolean);
      description = descParts.join(" | ");
    }
  }

  // 강화 대상(유입 Top10 제외 전체)에만 service/flower/상조 키워드 추가
  const idStr = String(id);
  const isEnrichmentTarget = parlor && !PARLOR_ENRICHMENT_EXCLUDE_IDS.ch.has(idStr);
  const baseKeywords = `${parlor?.companyname}, ${parlor?.companyname} 비용, ${parlor?.companyname}평균 예상 비용, ${parlor?.companyname} 무빈소, ${parlor?.companyname} 가족장, ${parlor?.companyname} 추천,
      ${parlor?.companyname} 시설, ${parlor?.companyname} 위치, ${parlor?.companyname} 주소, ${parlor?.companyname} 빈소, ${parlor?.companyname} 후기 ${parlor?.companyname} 영업시간`;
  const pageKeywords = isEnrichmentTarget
    ? `${baseKeywords}, ${parlor?.companyname} 장례 서비스, ${parlor?.companyname} 근조화환, ${parlor?.companyname} 상조`
    : baseKeywords;

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords,
      pageOgTitle: `${parlor?.companyname} 비용, 빈소, 이용시설, 위치 안내 (${district}) | 충청대전세종 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const result = await getParlorStaticProps(ctx);
  return "props" in result
    ? { ...result, props: { ...result.props, eskyPrice: getEskyPrice(ctx.params?.id) } }
    : result;
};

export default function ParlorItem({
  defaultParlor,
  eskyPrice,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  eskyPrice: EskyPriceData | null;
  pageOgTitle: string;
}) {
  const expParams = "&utm_content=ch&utm_medium=parlor_cta";
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu),
  );
  const binsos = defaultParlor.binsos || [];
  const parlorFaqSchema = buildFaqList([{
    question: `${defaultParlor.companyname} 오시는 길과 빈소 구성이 궁금합니다`,
    answer: `주소는 ${defaultParlor.address}입니다. ${
      binsos.length
        ? `빈소는 ${binsos.length}가지 규모로 ${Math.min(...binsos.map(b => b.pyeong || 0))}평부터 ${Math.max(...binsos.map(b => b.pyeong || 0))}평까지 운영하고 있습니다.`
        : `빈소 세부 정보는 고이장례연구소에 문의해 주세요.`
    }`,
  }]);
  const minPy = binsos.length
    ? Math.min(...binsos.map((b) => b.pyeong || 0))
    : 0;
  const maxPy = binsos.length
    ? Math.max(...binsos.map((b) => b.pyeong || 0))
    : 0;

  // 강화 분기 변수 (유입 Top10·비용제외 페이지만 기존 템플릿, 나머지 전부 강화)
  const idStr = String(defaultParlor.id);
  const isExcludeAll = excludeParlorList.includes(idStr);
  const isExcludePrice = excludeParlorPriceList.includes(idStr);
  const showEnrichedSections = !PARLOR_ENRICHMENT_EXCLUDE_IDS.ch.has(idStr) && !isExcludeAll;

  // 강화 페이지 전용 FAQ 동적 분기 (페이지 특성에 따라 3번째 다름)
  const isHospital =
    defaultParlor.companyname.includes("병원") ||
    defaultParlor.companyname.includes("의료원");
  const faqParkCnt = Number(defaultParlor.amenities?.parkcnt) || 0;
  const enrichedFaqKeys: FAQKey[] = ["binso_count", "binso_charge"];
  if (defaultParlor.managed_type === "공설") enrichedFaqKeys.push("gongsul_or_private");
  else if (isHospital) enrichedFaqKeys.push("hospital_or_independent");
  else if (faqParkCnt > 0 && faqParkCnt < 20) enrichedFaqKeys.push("parking");
  else enrichedFaqKeys.push("how_to_visit");

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <link
          rel="canonical"
          href={`https://www.ch-funeralportal.co.kr/parlor-items/${defaultParlor.id}/`}
          key="canonical"
        />
      </Head>
        <RichSearch data={buildParlorLocalBusinessSchema(defaultParlor)} />
        <RichSearch
          data={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "고이장례연구소",
                item: GOI_WEB_URL + "/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "장례식장 추천",
                item: GOI_WEB_URL + "/match/request/",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: `${sidoName} 장례식장`,
                item: `https://www.ch-funeralportal.co.kr/parlors-list/${sidoName} 의 장례식장 목록/`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: `${sigunguName} 장례식장`,
                item: `https://www.ch-funeralportal.co.kr/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
              },
              {
                "@type": "ListItem",
                position: 5,
                name: defaultParlor.companyname,
                item: `https://www.ch-funeralportal.co.kr/parlor-items/${defaultParlor.id}/`,
              },
            ],
          }}
        />
        <RichSearch data={mergeFaqSchemas(parlorFaqSchema, sangjoFaqRichSearch)} />

      <BTypeBreadCrumb
        firstItem={{
          text: "장례식장 추천",
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        currentItem={{ text: defaultParlor.companyname, href: `/parlor-items/${defaultParlor.id}/` }}
        goiHomeEvent={{
          domain: "ch",
          path: "prlr_item_intro",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      {/* 00 Hero - ok는 기존 grid, mismatch는 stack (사진을 h1 직후로 올림, HeroDataCard·HeroPills 제거) */}
      {showEnrichedSections ? (
        <Box component="header" style={{ padding: "32px 20px 40px" }}>
          <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
            <h1 style={{ fontSize: "28px", fontWeight: 700, lineHeight: 1.3, color: "#252525", margin: 0 }}>
              {defaultParlor.companyname}
            </h1>
            <Box
              component="address"
              style={{ marginTop: "8px", fontSize: "13px", color: "#686868", fontStyle: "normal" }}
            >
              {defaultParlor.address}
            </Box>
            {defaultParlor.images?.[0]?.url && (
              <Box
                component="img"
                src={defaultParlor.images[0].url}
                alt={`${defaultParlor.companyname} 전경`}
                style={{ marginTop: "20px", width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: "12px", display: "block" }}
              />
            )}
            <Stack direction="column" gap="10px" style={{ marginTop: "32px" }}>
              <Box
                component="a"
                href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
                onClick={() => customEvent({ domain: "ch", path: "prlr_item_intro", trigger_and_target: "uv_mth_req" })}
                style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", gap: "8px", padding: "18px", borderRadius: "12px", fontSize: "16px", fontWeight: 700, background: regionColor.c400, color: "#fff", textDecoration: "none" }}
              >
                <span>{defaultParlor.companyname} 전체 비용 확인하기</span>
                <ColorIcon name="arrow_right_small" color="#fff" size={18} />
              </Box>
            </Stack>
            <Box component="p" style={{ marginTop: "10px", fontSize: "12px", color: "#888", lineHeight: 1.5, margin: "10px 0 0" }}>
              ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
            </Box>
            <Box component="p" style={{ marginTop: "14px", fontSize: "13px", color: "#686868", lineHeight: 1.6 }}>
              근조화환 배송은{" "}
              <Box
                component="a"
                href={`${GOI_WEB_URL}/parlor/${defaultParlor.id}/?showFlowerModal=true&utm_source=dogsound`}
                style={{ color: regionColor.c500, textDecoration: "underline" }}
              >
                근조화환 보내기
              </Box>
              에서 신청하실 수 있습니다.
            </Box>
          </Box>
        </Box>
      ) : (
        <Box component="header" style={{ padding: "40px 20px 40px" }}>
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "32px",
            }}
            sx={{
              "@media (min-width: 960px)": {
                gridTemplateColumns: "2fr 1fr",
                gap: "56px",
                alignItems: "start",
              },
            }}
          >
            <Box>
              <h1
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "#252525",
                  margin: 0,
                }}
              >
                {defaultParlor.companyname}
              </h1>
              <Box
                component="address"
                style={{ marginTop: "8px", fontSize: "13px", color: "#686868", fontStyle: "normal" }}
              >
                {defaultParlor.address}
              </Box>
              <Box
                component="p"
                style={{
                  marginTop: "24px",
                  fontSize: "16px",
                  lineHeight: 1.75,
                  color: "#535353",
                }}
              >
                {sigunguName} 지역에 위치한{" "}
                <strong>{defaultParlor.companyname}</strong>은
                {binsos.length
                  ? ` ${minPy}평부터 ${maxPy}평 규모까지 총 ${binsos.length}개의 빈소를 운영합니다.`
                  : " 장례 서비스를 제공합니다."}{" "}
                빈소별 평형·사용료·예상 장례비용을{" "}
                <em style={{ fontStyle: "normal", color: regionColor.c500 }}>
                  e하늘 장사정보시스템(보건복지부) 공개 데이터
                </em>{" "}
                기준으로 안내합니다.
              </Box>
              <Stack
                direction="column"
                gap="10px"
                style={{ marginTop: "32px" }}
              >
                <Box
                  component="a"
                  href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
                  onClick={() => customEvent({ domain: "ch", path: "prlr_item_intro", trigger_and_target: "uv_mth_req" })}
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "18px",
                    borderRadius: "12px",
                    fontSize: "16px",
                    fontWeight: 700,
                    background: regionColor.c400,
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {defaultParlor.companyname} 비용 알아보기
                </Box>
              </Stack>
              <Box
                component="p"
                style={{ marginTop: "10px", fontSize: "12px", color: "#888", lineHeight: 1.5, margin: "10px 0 0" }}
              >
                ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
              </Box>
            </Box>
            <Box
              sx={{
                "@media (min-width: 960px)": { position: "sticky", top: "84px" },
              }}
            >
              {defaultParlor.images?.[0]?.url && (
                <Box
                  component="img"
                  src={defaultParlor.images[0].url}
                  alt={`${defaultParlor.companyname} 전경`}
                  style={{
                    width: "100%",
                    aspectRatio: "16/9",
                    objectFit: "cover",
                    borderRadius: "12px",
                    marginBottom: "16px",
                    display: "block",
                  }}
                />
              )}
              <HeroDataCard
                defaultParlor={defaultParlor}
                mainColor={regionColor}
                useTableMarkup
              />
            </Box>
          </Box>
          <HeroPills defaultParlor={defaultParlor} mainColor={regionColor} />
        </Box>
      )}

      {/* 빠르게 알아보기 (실험 key 6 종료, B안 전체 적용) */}
      <ParlorQuickInquiry
        companyName={defaultParlor.companyname}
        sido={defaultParlor.sido ?? ""}
        sigungu={defaultParlor.sigungu ?? ""}
        parlorId={defaultParlor.id}
        eventDomain="ch"
        eventPath="prlr_item_intro"
        mainColor={regionColor}
      />

      {/* 상황별 바로가기 (전체 id) */}
      {!isExcludeAll && (
        <ParlorIntentLinks
          companyName={defaultParlor.companyname}
          parlorId={defaultParlor.id}
          eventDomain="ch"
          eventPath="prlr_item_intro"
          mainColor={regionColor}
        />
      )}

      {/* ETypeCTA (위쪽) - ok에만 노출, mismatch는 Hero 자체 CTA로 충분 */}
      {!showEnrichedSections && (
        <ETypeCTA
          title="충청 장례 비용, 전문가가 직접 분석해드려요"
          subtitle="e하늘 공공 데이터 기반 실제 견적을 무료로 확인하세요"
          href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
          buttonText="장례 비용 확인하기"
          mainColor={regionColor}
          event={{ domain: "ch", path: "prlr_item_intro", trigger_and_target: "top_CTA_mth_req" }}
        />
      )}

      {/* 02 빈소 사용료 - ok는 ETypeBinsoPriceSummary, mismatch는 BinsoPriceTable */}
      <Box id="binso">
        {showEnrichedSections && !isExcludePrice ? (
          <Box style={{ padding: "40px 20px" }}>
            <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
              <Box
                component="h2"
                style={{ fontSize: "22px", fontWeight: 700, color: "#252525", marginBottom: "16px", lineHeight: 1.4 }}
              >
                {defaultParlor.companyname}
                <br />
                빈소 비용
              </Box>
              <BinsoPriceTable
                defaultParlor={defaultParlor}
                mainColor={regionColor}
              />
              <Box
                style={{
                  marginTop: "12px",
                  fontSize: "11px",
                  color: "#888",
                  whiteSpace: "pre-line",
                }}
              >
                {LEGAL_TEXTS.COST_VARIABILITY}
              </Box>
            </Box>
          </Box>
        ) : (
          <ETypeBinsoPriceSummary
            defaultParlor={defaultParlor}
            mainColor={regionColor}
            ctaHref={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
            ctaText="빈소 가격을 포함한 전체 장례 비용을 확인해보세요"
            event={{ domain: "ch", path: "prlr_item_intro", trigger_and_target: "uv_expert_mth_req" }}
          />
        )}
      </Box>

      {/* 03 예상 비용 */}
      {/* e하늘 식사, 장례용품 가격 (짝수 id SEO 실험) */}
      {eskyPrice && !isExcludeAll && !isExcludePrice && (
        <EskyPriceDetailSection companyName={defaultParlor.companyname} data={eskyPrice} mainColor={regionColor} />
      )}

      <EstimateSection defaultParlor={defaultParlor} mainColor={regionColor} />

      {/* 03-1 mismatch 강화 - 상조 + 인근 화장장 */}
      {showEnrichedSections && (
        <>
          <Box style={{ padding: "40px 0" }}>
            <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
              <Box
                component="h2"
                style={{ fontSize: "22px", fontWeight: 700, color: "#252525", marginBottom: "16px", padding: "0 20px" }}
              >
                상조 서비스 비용 (고이)
              </Box>
              <CTypeSangjoService
                defaultParlor={defaultParlor}
                mainColor={regionColor}
                event={{
                  domain: "ch",
                  path: "prlr_item_intro",
                  trigger_and_target: "uv_mth_req_btom",
                }}
              />
            </Box>
          </Box>
          <Box style={{ padding: "0 20px 40px" }}>
            <Box style={{ maxWidth: "720px", margin: "0 auto" }}>
              <NearbyCrematoria defaultParlor={defaultParlor} mainColor={regionColor} />
            </Box>
          </Box>
        </>
      )}

      {/* 03 CTA (아래쪽) - ok에만 노출 */}
      {!showEnrichedSections && (
        <>
          <ETypeCTA
            title="충청 장례 비용, 전문가가 직접 분석해드려요"
            subtitle="e하늘 공공 데이터 기반 실제 견적을 무료로 확인하세요"
            href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
            buttonText="장례 비용 확인하기"
            mainColor={regionColor}
            event={{ domain: "ch", path: "prlr_item_intro", trigger_and_target: "CTA_uv_mth_req" }}
          />
          <Box
            component="p"
            style={{ padding: "0 20px 20px", fontSize: "12px", color: "#888", lineHeight: 1.5, margin: 0 }}
          >
            ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
          </Box>
        </>
      )}

      {/* 04 편의시설 */}
      <ATypeAmenity defaultParlor={defaultParlor} />

      {/* 06 공설/사립 분기 */}
      <SupplementarySection
        defaultParlor={defaultParlor}
        mainColor={regionColor}
        ctaHref={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound${expParams}`}
        ctaText={`${defaultParlor.companyname} 기준 실제 견적을\n무료로 받아보실 수 있습니다`}
      />

      {/* 07 위치 및 교통 */}
      <Stack style={{ background: regionColor.c100, padding: "40px 20px" }}>
        <CTypeMap
          defaultParlor={defaultParlor}
          address={defaultParlor.address}
        />
      </Stack>
      <TrafficInfo defaultParlor={defaultParlor} />

      {/* 08 FAQ - ok는 기본, mismatch는 페이지 특성 분기 */}
      {showEnrichedSections ? (
        <ETypeFAQ defaultParlor={defaultParlor} mainColor={regionColor} faqKeys={enrichedFaqKeys} />
      ) : (
        <ETypeFAQ defaultParlor={defaultParlor} mainColor={regionColor} />
      )}

      
      {/* 주변 장례식장 */}
      <Stack style={{ padding: "40px 20px" }}>
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          sigungu={String(defaultParlor.sigungu)}
          currentParlorId={defaultParlor.id}

          currentLat={Number((defaultParlor as any).latitude)}

          currentLng={Number((defaultParlor as any).longitude)}
          title={`${defaultParlor.companyname} 근처 장례식장`}
          CardViewType={(res) => (
            <ATypeCardView
              key={res.companyname}
              name={res.companyname}
              href={`/parlor-items/${res.id}/`}
              descriptions={[
                ...(res.distanceLabel ? [`${defaultParlor.companyname}에서 ${res.distanceLabel}`] : []),
                `빈소 평균 비용 : ${Number(res.binso_average_charge).toLocaleString()}원`,
              ]}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "ch",
                path: "prlr_item_intro",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
